import React, { useEffect, useRef, useMemo, useState } from "react";
import { useTable, useRowSelect } from "react-table";
import { columnBaseTable, conventionalTable } from "./tableHeaderTbc";
import { useDispatch } from "react-redux";
import { setSelectedFlatRows, updateGroupings, updateLedgers } from "../../../redux/actions/BookClosure/actionBookClosure";

import { formatAmount } from "../../../utils/formatAmount";
import tablecss from "../../../myfin_v2/css/table.module.css";
import commoncss from "../../../myfin_v2/css/common.module.css";
import { mapLedger } from "../../Services/ApiHandler";
import TbMultiSelect from "../TBC/TbMultiSelect";


const TbConventionalMode = React.memo(({ lineItems, setDragData, searchText, groupings, bookClosureTbId }) => {
    const dispatch = useDispatch();

    const [tableData, setTableData] = useState(lineItems);
    const [showMultiMapping, setShowMultiMapping] = useState(false);

    useEffect(() => {
        const mappedData = lineItems.map(item => ({
            ...item,
            fs_grp_cy: item.FSGroupCY || "",
            note_grp_cy: item.notesGroupCY || "",
            sub_grp_cy: item.subGroupCY || "",
            fs_grp_py: item.FSGroupPY || "",
            note_grp_py: item.notesGroupPY || "",
            sub_grp_py: item.subGroupPY || "",
        }));
        setTableData(mappedData);
    }, [lineItems]);



    const groupingOptions = useMemo(() => {
        return groupings.map((lvl1) => ({
            fs_grp: lvl1.name,
            notes: lvl1.subgroup.map((lvl2) => ({
                note_grp: lvl2.name,
                sub: lvl2.subgroup.map((lvl3) => lvl3.name),
            })),
        }));
    }, [groupings]);

    const columns = useMemo(() => {
        const dropdownColumns = [
            "fs_grp_cy",
            "note_grp_cy",
            "sub_grp_cy",
            "fs_grp_py",
            "note_grp_py",
            "sub_grp_py",
        ];

        return conventionalTable.map((col) => {
            if (!dropdownColumns.includes(col.accessor)) return col;

            return {
                ...col,
                Cell: ({ row, value }) => {
                    let optionsList = [];
                    let disabled = false;

                    const rowData = tableData[row.index] || {};

                    // CY
                    if (col.accessor === "fs_grp_cy") {
                        optionsList = groupingOptions.map((o) => o.fs_grp);
                    } else if (col.accessor === "note_grp_cy") {
                        const fsValue = rowData.fs_grp_cy;
                        optionsList = groupingOptions.find((o) => o.fs_grp === fsValue)?.notes.map((n) => n.note_grp) || [];
                        disabled = !fsValue;
                    } else if (col.accessor === "sub_grp_cy") {
                        const fsValue = rowData.fs_grp_cy;
                        const noteValue = rowData.note_grp_cy;
                        optionsList =
                            groupingOptions
                                .find((o) => o.fs_grp === fsValue)
                                ?.notes.find((n) => n.note_grp === noteValue)?.sub || [];
                        disabled = !noteValue;
                    }

                    // PY
                    if (col.accessor === "fs_grp_py") {
                        optionsList = groupingOptions.map((o) => o.fs_grp);
                    } else if (col.accessor === "note_grp_py") {
                        const fsValue = rowData.fs_grp_py;
                        optionsList = groupingOptions.find((o) => o.fs_grp === fsValue)?.notes.map((n) => n.note_grp) || [];
                        disabled = !fsValue;
                    } else if (col.accessor === "sub_grp_py") {
                        const fsValue = rowData.fs_grp_py;
                        const noteValue = rowData.note_grp_py;
                        optionsList =
                            groupingOptions
                                .find((o) => o.fs_grp === fsValue)
                                ?.notes.find((n) => n.note_grp === noteValue)?.sub || [];
                        disabled = !noteValue;
                    }

                    return (
                        <select
                            className={`${commoncss.custSelectDropdown}`}
                            value={value || ""}
                            onChange={(e) => handleChange(row.index, col.accessor, e.target.value)}
                            disabled={disabled}
                        >
                            <option value="">Select</option>
                            {optionsList.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    );
                },
            };
        });
    }, [conventionalTable, groupingOptions, tableData]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
    } = useTable(
        {
            columns,
            data: tableData,
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
            ]);
        }
    );

    const selectedRowsMemo = useMemo(
        () => selectedFlatRows.map((row) => row.original),
        [selectedFlatRows]
    );

    useEffect(() => {
        dispatch(setSelectedFlatRows(selectedRowsMemo));
    }, [selectedRowsMemo, dispatch]);

    
    useEffect(() => {
        if (selectedFlatRows.length >= 2) {
            setShowMultiMapping(true);
        } else {
            setShowMultiMapping(false);
        }
    }, [selectedFlatRows]);

    const handleChange = async (rowIndex, columnId, value) => {
        const updatedData = [...tableData];
        updatedData[rowIndex] = { ...updatedData[rowIndex], [columnId]: value };

        // Reset dependent dropdowns
        if (columnId === "fs_grp_cy") {
            updatedData[rowIndex].note_grp_cy = "";
            updatedData[rowIndex].sub_grp_cy = "";
        }
        if (columnId === "note_grp_cy") updatedData[rowIndex].sub_grp_cy = "";
        if (columnId === "fs_grp_py") {
            updatedData[rowIndex].note_grp_py = "";
            updatedData[rowIndex].sub_grp_py = "";
        }
        if (columnId === "note_grp_py") updatedData[rowIndex].sub_grp_py = "";

        setTableData(updatedData);

        if (columnId === "sub_grp_cy" || columnId === "sub_grp_py") {
            const ledger = updatedData[rowIndex];

            const ledgerPayload = { ledgerID: ledger._id };

            if (ledger.fs_grp_cy) ledgerPayload.FSGroupCY = ledger.fs_grp_cy;
            if (ledger.note_grp_cy) ledgerPayload.notesGroupCY = ledger.note_grp_cy;
            if (ledger.sub_grp_cy) ledgerPayload.subGroupCY = ledger.sub_grp_cy;

            if (ledger.fs_grp_py) ledgerPayload.FSGroupPY = ledger.fs_grp_py;
            if (ledger.note_grp_py) ledgerPayload.notesGroupPY = ledger.note_grp_py;
            if (ledger.sub_grp_py) ledgerPayload.subGroupPY = ledger.sub_grp_py;

            const payload = { ledgers: [ledgerPayload] };

            try {
                const response = await mapLedger(bookClosureTbId, payload);
                dispatch(updateGroupings(response.data.updatedGroupings));
                dispatch(updateLedgers(response.data.updatedLedgers));
            } catch (error) {
                console.error("Failed to save ledger mapping:", error);
            }
        }
    };

    console.log("showMultiMapping",showMultiMapping);

    return (
        <>
            <div className={tablecss.tableContainer}>
                <TbMultiSelect
                    show={showMultiMapping}
                    onClose={() => setShowMultiMapping(false)}
                    groupings={groupings}
                    selectedLedgers={selectedRowsMemo}
                    bookClosureTbId={bookClosureTbId}
                />
                <table className={tablecss.customTable} {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column, i) => (
                                    <th
                                        key={column.id}
                                        {...column.getHeaderProps()}
                                        style={{
                                            ...column.Style,
                                            ...(i === 0
                                                ? { width: "35px", minWidth: "35px", maxWidth: "35px" }
                                                : {}),
                                        }}
                                        className={`font-inter text-left`}
                                    >
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row, ind) => {
                            prepareRow(row);
                            if (
                                row.original.deleted ||
                                !row.original.ledgerCode.toLowerCase().includes(searchText.toLowerCase()) &&
                                !row.original.ledgerName.toLowerCase().includes(searchText.toLowerCase()) &&
                                !String(row.original.periodBeginingBalance).includes(searchText.toLowerCase()) &&
                                !String(row.original.periodEndingBalance).includes(searchText.toLowerCase())
                            ) return null;
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    className={
                                        row.original.deleted
                                            ? "bg-danger"
                                            : row.original?.FSGroupCY && row.original?.FSGroupPY
                                                ? commoncss.doubleMapColor
                                                : row.original?.FSGroupCY || row.original?.FSGroupPY
                                                    ? commoncss.singleMapColor
                                                    : ""
                                    }
                                >
                                    {row.cells.map((cell, i) => (
                                        <td
                                            style={{ background: "transparent" }}
                                            key={cell.column.id}
                                            {...cell.getCellProps()}
                                            className={`${["ledgerCode", "ledgerName"].includes(cell.column.id)
                                                ? "text-left pl-3"
                                                : "selection" === cell.column.id
                                                    ? "text-center"
                                                    : "text-right pr-3"
                                                }`}
                                        >
                                            {[
                                                "periodBeginingBalance",
                                                "periodEndingBalance",
                                            ].includes(cell.column.id)
                                                ? formatAmount(cell.value)
                                                : cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
});

export default TbConventionalMode;

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = useRef();
        const resolvedRef = ref || defaultRef;

        useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <input type="checkbox" ref={resolvedRef} {...rest} />;
    }
);
